"use strict";
/**
 * Canonical resolution of the PCI Booking API host.
 *
 * This is the single source of truth for mapping the static hosting domain to
 * the service (API) domain. Every app that talks to the TravelSwitch API should
 * resolve its base URL through here rather than re-deriving the hostname, so
 * the two cannot drift apart per app.
 *
 * The apps keep their own routing tables (which hostnames they recognise, and
 * what they call each environment) because those legitimately differ; only the
 * static -> service mapping lives here.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.localApiBaseUrl = exports.resolvePciApiTarget = exports.resolveServiceHostname = exports.getStageSuffix = exports.isLocalHostname = exports.LOCAL_API_ORIGIN = void 0;
/** API origin used by every app's local dev setup. */
exports.LOCAL_API_ORIGIN = 'https://localhost:44314';
/** Matches static.pcibooking.net as well as static-<stage>.pcibooking.net. */
const STATIC_HOST_PATTERN = /^static(-\w+)?\.pcibooking\.net$/;
/** True when the page is served from a local dev server. */
function isLocalHostname(hostname) {
    return hostname === 'localhost' || hostname === '127.0.0.1';
}
exports.isLocalHostname = isLocalHostname;
/**
 * The stage suffix of a static hosting host, including its leading hyphen:
 * '' for static.pcibooking.net, '-qa' for static-qa.pcibooking.net, and null
 * for any host that is not a static hosting domain.
 */
function getStageSuffix(hostname) {
    const match = hostname.match(STATIC_HOST_PATTERN);
    if (!match) {
        return null;
    }
    return match[1] || '';
}
exports.getStageSuffix = getStageSuffix;
/**
 * Maps the static hosting domain to the service (API) domain:
 *   static.pcibooking.net         -> service.pcibooking.net
 *   static-qa.pcibooking.net      -> service-qa.pcibooking.net
 *   static-staging.pcibooking.net -> service-staging.pcibooking.net
 *   static-dev.pcibooking.net     -> service-dev.pcibooking.net
 *
 * Any other hostname is returned unchanged - the static site is only ever a
 * CDN, so a request left pointing at it is answered with the SPA's index.html
 * and HTTP 200 rather than API data.
 */
function resolveServiceHostname(hostname) {
    const stageSuffix = getStageSuffix(hostname);
    return stageSuffix === null ? hostname : `service${stageSuffix}.pcibooking.net`;
}
exports.resolveServiceHostname = resolveServiceHostname;
/**
 * Resolves the API target for a PCI Booking host, or null when the current
 * hostname is not a pcibooking.net domain (the caller then applies its own
 * routing rules).
 *
 * @param pathSegment API path to append, e.g. '/api/verify'
 */
function resolvePciApiTarget(pathSegment, options = {}) {
    const hostname = options.hostname ?? window.location.hostname;
    if (!hostname.includes('pcibooking.net')) {
        return null;
    }
    const protocol = options.protocol ?? window.location.protocol;
    const apiHostname = resolveServiceHostname(hostname);
    const stageSuffix = getStageSuffix(hostname);
    return {
        baseUrl: `${protocol}//${apiHostname}${pathSegment}`,
        hostname: apiHostname,
        stage: stageSuffix ? stageSuffix.substring(1) : ''
    };
}
exports.resolvePciApiTarget = resolvePciApiTarget;
/** Base URL for an API path segment on the local dev API. */
function localApiBaseUrl(pathSegment) {
    return `${exports.LOCAL_API_ORIGIN}${pathSegment}`;
}
exports.localApiBaseUrl = localApiBaseUrl;
